import { useState } from "react";
import type { PlanEvent } from "../../../shared/types";
import type { RecurrenceToastState } from "../components/dialogs/RecurrenceActionToast";

export type PendingRecurrence =
  | { readonly kind: "delete"; readonly event: PlanEvent }
  | {
      readonly kind: "update";
      readonly event: PlanEvent;
      readonly updates: Partial<
        Pick<
          PlanEvent,
          | "date"
          | "startTime"
          | "endTime"
          | "title"
          | "description"
          | "notes"
          | "tags"
          | "recurrence"
          | "reminderMinutes"
          | "category"
        >
      >;
    };

export type PendingResync = PlanEvent;

export interface FormAnchor {
  /** Right edge of the clicked column (viewport px) */
  readonly colRight: number;
  /** Left edge of the clicked column (viewport px) */
  readonly colLeft: number;
  /** Top of the clicked time slot (viewport px) */
  readonly slotTop: number;
}

interface WeekCalendarState {
  showAddForm: boolean;
  formStartTime: string | null;
  formEndTime: string | null;
  formAnchor: FormAnchor | null;
  editingEvent: PlanEvent | null;
  showFullEditForm: boolean;
  pendingRecurrence: PendingRecurrence | null;
  pendingResync: PendingResync | null;
  recurrenceToast: RecurrenceToastState | null;
}

interface WeekCalendarStateActions {
  setShowAddForm: (v: boolean) => void;
  setFormStartTime: (v: string | null) => void;
  setFormEndTime: (v: string | null) => void;
  setFormAnchor: (v: FormAnchor | null) => void;
  setEditingEvent: (v: PlanEvent | null) => void;
  setShowFullEditForm: (v: boolean) => void;
  setPendingRecurrence: (v: PendingRecurrence | null) => void;
  setPendingResync: (v: PendingResync | null) => void;
  setRecurrenceToast: (v: RecurrenceToastState | null) => void;
}

export function useWeekCalendarState(): WeekCalendarState &
  WeekCalendarStateActions {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formStartTime, setFormStartTime] = useState<string | null>(null);
  const [formEndTime, setFormEndTime] = useState<string | null>(null);
  const [formAnchor, setFormAnchor] = useState<FormAnchor | null>(null);
  const [editingEvent, setEditingEvent] = useState<PlanEvent | null>(null);
  const [showFullEditForm, setShowFullEditForm] = useState(false);
  const [pendingRecurrence, setPendingRecurrence] =
    useState<PendingRecurrence | null>(null);
  const [pendingResync, setPendingResync] = useState<PendingResync | null>(
    null,
  );
  const [recurrenceToast, setRecurrenceToast] =
    useState<RecurrenceToastState | null>(null);

  return {
    showAddForm,
    formStartTime,
    formEndTime,
    formAnchor,
    editingEvent,
    showFullEditForm,
    pendingRecurrence,
    pendingResync,
    recurrenceToast,
    setShowAddForm,
    setFormStartTime,
    setFormEndTime,
    setFormAnchor,
    setEditingEvent,
    setShowFullEditForm,
    setPendingRecurrence,
    setPendingResync,
    setRecurrenceToast,
  };
}
